import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { TabbedFormTabs } from 'react-admin';

const EventTabNavContext = createContext(null);

/**
 * Lets the create toolbar drive TabbedForm tabs without changing the URL
 * (URL sync remounts Create on Back to /create and blanks the form).
 */
export const EventTabNavProvider = ({ tabCount, children }) => {
  const goToRef = useRef(null);
  const [currentTab, setCurrentTab] = useState(0);

  const registerGoTo = useCallback((fn) => {
    goToRef.current = fn;
  }, []);

  const goToTab = useCallback((index) => {
    const next = Math.max(0, Math.min(tabCount - 1, index));
    goToRef.current?.(next);
    setCurrentTab(next);
  }, [tabCount]);

  const value = useMemo(
    () => ({
      tabCount,
      currentTab,
      setCurrentTab,
      goToTab,
      registerGoTo,
    }),
    [tabCount, currentTab, goToTab, registerGoTo],
  );

  return <EventTabNavContext.Provider value={value}>{children}</EventTabNavContext.Provider>;
};

export const useEventTabNav = () => {
  const ctx = useContext(EventTabNavContext);
  if (!ctx) {
    throw new Error('useEventTabNav must be used within EventTabNavProvider');
  }
  return ctx;
};

/**
 * Wires TabbedForm's internal onChange into our nav context for Next/Back.
 */
export const EventFormTabs = (props) => {
  const { registerGoTo, setCurrentTab } = useEventTabNav();
  const { onChange, value } = props;

  useEffect(() => {
    registerGoTo((index) => {
      onChange?.(null, index);
    });
  }, [onChange, registerGoTo]);

  useEffect(() => {
    if (typeof value === 'number') {
      setCurrentTab(value);
    }
  }, [value, setCurrentTab]);

  return <TabbedFormTabs {...props} syncWithLocation={false} />;
};
